import React, { useEffect, useState } from 'react';
import './App.css';
import 'fontsource-roboto';
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from '@mui/material/styles';
import { Container } from '@mui/material';
import { FirestoreProvider } from 'firebaseHooks/FirestoreContext';
import { FunctionsProvider } from 'firebaseHooks/FunctionsContext';
import { AuthProvider } from 'firebaseHooks/AuthContext';
import { isSsoHost } from 'sso/ssoClient';
import { AnalyticsProvider } from 'firebaseHooks/AnalyticsContext';
import { StorageProvider } from 'firebaseHooks/StorageContext';
import { InventoryQueueRegistryProvider } from 'components/operation/hooks/useInventoryQueueRegistry';
import { RemoteConfigProvider } from 'firebaseHooks/RemoteConfigContext';
import { getAuth, connectAuthEmulator } from 'firebase/auth';

import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import CssBaseline from '@mui/material/CssBaseline';
import LoadingPage from 'components/LoadingPage';
import Home from 'components/Home';
import SentryScopeSync from 'components/SentryScopeSync';
import { initializeApp } from 'firebase/app';
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
} from 'firebase/firestore';
import { getAnalytics, setAnalyticsCollectionEnabled } from 'firebase/analytics';

// const host = '192.168.1.129';
const host = 'localhost';

const firebaseConfig = {
  apiKey: 'AIzaSyCa9rLf4xmacQg6xdoxOUxrWFrz7N_34W4',
  authDomain: 'inventory.labox-apps.com',
  projectId: 'labox-ws',
  storageBucket: 'labox-ws.appspot.com',
  messagingSenderId: '669483452439',
  appId: '1:669483452439:web:cd60feb77e4ad128297452',
  measurementId: 'G-FM7D8XN6KB',
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#437A89',
      contrastText: '#fff',
    },
    secondary: {
      main: '#73a9b9',
      contrastText: '#fff',
    },
    text: {
      primary: '#3f4d5a',
    },
    info: {
      main: '#437A89',
      contrastText: '#fff',
    },
    ourGreen: {
      main: '#99BD5B',
      contrastText: '#fff',
    },
    flashyGreen: {
      main: '#1db954',
      contrastText: '#fff',
    },
    ourOrange: {
      main: '#f4ad45',
      contrastText: '#fff',
    },
    gold: {
      main: '#FFD700',
      contrastText: '#fff',
    },
    darkgray: {
      main: '#0000008a',
      contrastText: '#fff',
    },
    mediumgray: {
      main: '#8585858a',
      contrastText: '#fff',
    },
    gray: {
      main: '#d8d8d8',
      contrastText: '#fff',
    },
    lightGray: {
      main: '#f0f0f0',
      contrastText: '#000',
    },
  },
});

// Initialize all Firebase services at once
function initializeFirebaseServices() {
  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const functions = getFunctions(app, 'europe-west1');
  const storage = getStorage(app);
  const analyticsInstance = getAnalytics(app);

  // Initialize Firestore with proper settings
  const baseSettings = {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  };

  // Add long polling configuration
  const savedSetting = localStorage.getItem('forceLongPolling');
  const forceLongPolling = savedSetting === 'true';
  if (forceLongPolling) {
    baseSettings.experimentalForceLongPolling = true;
  } else {
    baseSettings.experimentalAutoDetectLongPolling = true;
  }

  const fsSettings = (process.env.NODE_ENV === 'development')
    ? {
      ...baseSettings,
      host: `${host}:4401`,
      ssl: false,
    } : baseSettings;

  const firestore = initializeFirestore(app, fsSettings);

  // Set up emulators in development
  if (process.env.NODE_ENV === 'development') {
    connectAuthEmulator(auth, `http://${host}:9099`);
    connectFunctionsEmulator(functions, host, 5001);
    connectStorageEmulator(storage, host, 9199);
    setAnalyticsCollectionEnabled(analyticsInstance, false);
  }

  return {
    app, auth, functions, storage, firestore,
  };
}

// Initialize services before rendering, so they are available to the app
const services = initializeFirebaseServices();

function App() {
  return (
    <div className="App" style={{ whiteSpace: 'pre-line' }}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Container>
            <CssBaseline />
            <AuthProvider
              app={services.app}
              ssoEnabled={isSsoHost() || process.env.NODE_ENV === 'development'}
            >
              <RemoteConfigProvider app={services.app}>
                <AnalyticsProvider app={services.app}>
                  <FirestoreProvider app={services.app}>
                    <InventoryQueueRegistryProvider>
                      <FunctionsProvider app={services.app}>
                        <StorageProvider app={services.app}>
                          <SentryScopeSync />
                          <Core />
                        </StorageProvider>
                      </FunctionsProvider>
                    </InventoryQueueRegistryProvider>
                  </FirestoreProvider>
                </AnalyticsProvider>
              </RemoteConfigProvider>
            </AuthProvider>
          </Container>
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
  );
}

function Core() {
  const [status, setStatus] = useState('initial');

  useEffect(() => {
    if (status === 'initial') {
      setStatus('loaded');
    }
  }, [status]);

  if (status !== 'loaded') {
    return <LoadingPage />;
  }

  return <Home />;
}

export default App;
